import React, { useState, useEffect, useCallback } from 'react';
import { getInvestidores, deleteInvestidor, addInvestidor } from '../services/api';
import InvestidorEditModal from '../components/InvestidorEditModal';
import {
    Container,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Autocomplete } from '@mui/material';

interface Investidor {
    id: number;
    nome: string;
    cpf_cnpj: string;
    contato: string;
}

const InvestidoresPage: React.FC = () => {
    const [investidores, setInvestidores] = useState<Investidor[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [investidorToEdit, setInvestidorToEdit] = useState<Investidor | null>(null);

    // Add Modal State
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [newNome, setNewNome] = useState('');
    const [newCpfCnpj, setNewCpfCnpj] = useState('');
    const [newContato, setNewContato] = useState('');

    const fetchInvestidores = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getInvestidores();
            setInvestidores(data || []);
        } catch (err) {
            console.error('Erro ao buscar investidores:', err);
            alert('Erro ao buscar dados dos investidores.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInvestidores();
    }, [fetchInvestidores]);

    const handleDelete = async (id: number) => {
        if (window.confirm('Tem certeza que deseja excluir este investidor?')) {
            try {
                await deleteInvestidor(id);
                fetchInvestidores();
            } catch (error) {
                alert('Erro ao excluir investidor.');
            }
        }
    };

    const handleEditClick = (investidor: Investidor) => {
        setInvestidorToEdit(investidor);
        setEditModalOpen(true);
    };

    const handleAddClick = () => {
        setNewNome('');
        setNewCpfCnpj('');
        setNewContato('');
        setAddModalOpen(true);
    };

    const handleSaveNewInvestidor = async () => {
        if (!newNome.trim() || !newCpfCnpj.trim()) {
            alert('Nome e CPF/CNPJ são obrigatórios.');
            return;
        }

        try {
            await addInvestidor({
                nome: newNome,
                cpf_cnpj: newCpfCnpj,
                contato: newContato
            });
            setAddModalOpen(false);
            fetchInvestidores();
            window.location.reload();
        } catch (error) {
            console.error('Erro ao adicionar investidor:', error);
            alert('Erro ao adicionar investidor.');
        }
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                    Investidores
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ borderRadius: 2 }}
                >
                    Novo Investidor
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                    <CircularProgress />
                </Box>
            ) : investidores.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">Nenhum investidor encontrado.</Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={handleAddClick}>
                        Adicionar o primeiro investidor
                    </Button>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={2}>
                    <Table sx={{ minWidth: 650 }} aria-label="tabela de investidores">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell><strong>Nome</strong></TableCell>
                                <TableCell><strong>CPF / CNPJ</strong></TableCell>
                                <TableCell><strong>Contato</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {investidores.map((investidor) => (
                                <TableRow
                                    key={investidor.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: 'action.hover' } }}
                                >
                                    <TableCell component="th" scope="row">
                                        {investidor.nome}
                                    </TableCell>
                                    <TableCell>{investidor.cpf_cnpj}</TableCell>
                                    <TableCell>{investidor.contato || '-'}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => handleEditClick(investidor)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton size="small" color="error" onClick={() => handleDelete(investidor.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add Modal */}
            <Dialog open={isAddModalOpen} onClose={() => setAddModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Adicionar Novo Investidor</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            freeSolo
                            options={investidores.map((option) => option.nome)}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {option} <span style={{ color: 'gray', fontSize: '0.8em', marginLeft: '8px' }}>(Cadastrado)</span>
                                </li>
                            )}
                            value={newNome}
                            onInputChange={(event, newInputValue) => {
                                setNewNome(newInputValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    label="Nome"
                                    fullWidth
                                    required
                                />
                            )}
                        />
                        <TextField
                            label="CPF/CNPJ"
                            fullWidth
                            value={newCpfCnpj}
                            onChange={(e) => setNewCpfCnpj(e.target.value)}
                            required
                        />
                        <TextField
                            label="Contato (Telefone/Email)"
                            fullWidth
                            value={newContato}
                            onChange={(e) => setNewContato(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddModalOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveNewInvestidor} variant="contained">Adicionar</Button>
                </DialogActions>
            </Dialog>

            <InvestidorEditModal
                investidor={investidorToEdit}
                open={isEditModalOpen}
                onInvestidorUpdated={() => {
                    fetchInvestidores();
                    window.location.reload();
                }}
            />
        </Container>
    );
};

export default InvestidoresPage;